import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Container } from "@/components/site/Layout";
import { FlagRule } from "@/components/site/Logo";
import { useHeroVideo, HeroVideo, VideoToggle } from "@/components/site/HeroVideo";
import { SectionHead, ClaimBadge, Reveal, MaskedLines } from "@/components/site/Primitives";
import { StatusTimeline, Frame } from "@/components/site/Modules";
import { FAQ, GLANCE, FACILITY_COPY } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";

const BUILD = [
  { t: "Crude intake", d: "Designed around U.S. light shale crude (47° API average design basis) delivered via port and rail.", type: "fact" },
  { t: "Refining units", d: "Commercially proven refining technologies. Axens is technology licensor; Process Consulting Services is designing the crude distillation unit (Apr 2026).", type: "dated" },
  { t: "Power", d: "Designed to use hydrogen-fired turbines with hydrogen produced in the refining process.", type: "projected" },
  { t: "Products out", d: "Ultra-low sulfur diesel, jet fuel and specialty gasoline, shipped by water, rail and road.", type: "projected" },
];

const FactCol = ({ type, title, items }) => (
  <Reveal className="hairline bg-[#0B1D4F] p-7 md:p-9">
    <ClaimBadge type={type} testId={`split-${type}-badge`} />
    <h3 className="font-display mt-4 text-3xl font-extrabold uppercase">{title}</h3>
    <ul className="mt-6 space-y-5">
      {items.map((g) => (
        <li key={g.key} className="border-t border-white/10 pt-4">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-slate-400">{g.label}</p>
          <p className="mt-1 text-xl font-semibold">{g.value}</p>
          <p className="mt-1 text-sm text-slate-300">{g.detail}</p>
        </li>
      ))}
    </ul>
  </Reveal>
);

const FacilityHero = () => {
  const video = useHeroVideo();
  return (
    <section data-testid="facility-hero" className="grain relative mt-[116px] flex min-h-[70svh] items-end overflow-hidden text-white">
      <div className="absolute inset-0"><HeroVideo video={video} testId="facility-hero-video" /></div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#061235] via-[#061235]/50 to-[#001870]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#061235]/85 via-[#061235]/35 to-transparent" />
      <div className="relative mx-auto grid w-full max-w-[1400px] gap-8 px-5 pb-14 pt-16 md:px-10 lg:grid-cols-12 lg:pb-20">
        <div className="min-w-0 lg:col-span-8">
          <h1 className="font-display text-5xl font-bold uppercase leading-[0.9] sm:text-6xl lg:text-[6.5rem]"><MaskedLines lines={["The Facility"]} /></h1>
          <p className="mt-6 max-w-2xl text-base text-white/90 md:text-lg">{FACILITY_COPY.intro}</p>
        </div>
        <VideoToggle video={video} testId="facility-video-toggle" className="min-w-0 lg:col-span-4 lg:self-end lg:justify-end" />
      </div>
      <FlagRule className="absolute inset-x-0 bottom-0 opacity-90" />
    </section>
  );
};

export default function Facility() {
  useSeo({
    title: "Brownsville Facility | America First Refining",
    description: "The America First Refining complex at the Port of Brownsville, Texas: site, port access, dated milestones, projected outputs and FAQ.",
    path: "/brownsville-facility",
    jsonLd: { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  });
  return (
    <div data-testid="facility-page">
      <FacilityHero />
      <Container testId="engine-section">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">Brownsville: an emerging economic engine powered by a deepwater port</h2>
            <div className="mt-6 space-y-4 leading-relaxed">{FACILITY_COPY.engine.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}</div>
            <div className="mt-8 border-l-4 border-[#AD7F27] bg-[#f3f5fa] p-6">
              <ClaimBadge type="projected" testId="growth-badge" />
              <p className="mt-3 leading-relaxed">{FACILITY_COPY.growth}</p>
            </div>
          </Reveal>
          <Frame src="/img/brownsville-map.jpg" alt="Map of Brownsville, Texas showing the Port of Brownsville location" className="mx-auto h-[420px] w-full max-w-[420px] lg:col-span-5 [&_img]:object-contain [&_img]:bg-white" />
        </div>
      </Container>
      <Container testId="port-section" tone="soft">
        <Reveal className="max-w-4xl">
          <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">Port of Brownsville: the port that works</h2>
          <div className="mt-6 space-y-4 leading-relaxed">{FACILITY_COPY.port.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}</div>
        </Reveal>
        <div className="mt-12 overflow-x-auto bg-white p-4 md:p-8" data-lenis-prevent>
          <img src="/img/facility-graphic-desktop.svg" alt="AFR graphic: Port of Brownsville location and logistics connections" className="mx-auto min-w-[720px] max-w-full" />
        </div>
      </Container>
      <div className="mx-auto max-w-[1400px] px-5 pt-20 md:px-10 md:pt-28">
        <Frame src="/img/afr-site-2026-03.jpg" alt="Artist rendering of the AFR refinery site with storage tanks and the ship channel" caption="Artist rendering · AFR, March 2026. Final layout subject to engineering." className="h-[300px] md:h-[620px]" />
      </div>
      <Container testId="what-section">
        <SectionHead eyebrow="01 · What is being built" title="Crude in, fuels out" body="A plain description of the process. Unit-level details stay placeholders until the engineering contractor's approved summary is available." />
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-4">
          {BUILD.map((b, i) => (
            <Reveal key={b.t} delay={i * 0.06} className="bg-[#0B1D4F] p-7">
              <div data-testid={`build-step-${i}`}>
                <p className="font-display text-6xl font-black text-[#D4A24C]">0{i + 1}</p>
                <h3 className="font-display mt-4 text-2xl font-bold uppercase">{b.t}</h3>
                <p className="mt-3 text-sm text-slate-300">{b.d}</p>
                <div className="mt-5"><ClaimBadge type={b.type} testId={`build-step-${i}-badge`} /></div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
      <Container id="timeline" testId="facility-timeline-section" tone="soft">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="02 · Timeline" title="Where things stand" /></div>
          <div className="lg:col-span-8"><StatusTimeline /></div>
        </div>
      </Container>
      <Container testId="fact-vs-projected-section">
        <SectionHead eyebrow="03 · Facts vs projections" title="Two columns, never mixed" body="Projected outputs sit in their own column so nobody reads a target as an achievement." />
        <div className="grid gap-6 md:grid-cols-2">
          <FactCol type="fact" title="True today" items={GLANCE.filter((g) => g.type !== "projected")} />
          <FactCol type="projected" title="Projected" items={GLANCE.filter((g) => g.type === "projected")} />
        </div>
      </Container>
      <Container id="faq" testId="faq-section" tone="soft">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="04 · FAQ" title="Common questions" /></div>
          <Accordion type="single" collapsible className="lg:col-span-8" data-testid="faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`f${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display py-6 text-left text-xl font-bold uppercase hover:no-underline md:text-2xl">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-base text-slate-300">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Container>
    </div>
  );
}
